import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./DashboardPage.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIconPack } from "../../context/IconPackContext";
import { useModules } from "../../context/ModulesContext";
import { useMenu } from "../../context/MenuContext"; 
import BlockGridInput from "../../components/BlockGridInput/BlockGridInput";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { ToastService } from "../../service/toast.service";

const STORAGE_KEY_SYSTEM = "dashboard_system_order";
const STORAGE_KEY_MODULES = "dashboard_modules_order";
const STORAGE_KEY_REPORTS = "dashboard_reports_order";

export default function DashboardPage() {
  const { t } = useLanguage();
  const { getIcon } = useIconPack();
  const navigate = useNavigate();
  const { modules, loading } = useModules();
  const { menuSettings } = useMenu(); 
  const [isEditMode, setIsEditMode] = useState(false);
  const [systemOrder, setSystemOrder] = useState<string[]>([]);
  const [modulesOrder, setModulesOrder] = useState<string[]>([]);
  const [reportsOrder, setReportsOrder] = useState<string[]>([]);

  const systemModules = modules.filter(m => 
    m.id !== 'root-folder' && 
    m.is_system === true
  );

  const regularModules = modules.filter(m => 
    m.id !== 'root-folder' && 
    !m.is_system &&
    m.table_name !== 'report'
  );

  const reportModules = modules.filter(m => 
    m.table_name === 'report'
  );

  useEffect(() => {
    // порядок системных модулей
    const savedSystemOrder = localStorage.getItem(STORAGE_KEY_SYSTEM);
    if (savedSystemOrder) {
      try {
        setSystemOrder(JSON.parse(savedSystemOrder));
      } catch (e) {
        console.error("Failed to parse saved system order:", e);
        initializeDefaultSystemOrder();
      }
    } else {
      initializeDefaultSystemOrder();
    }

    // порядок обычных модулей
    const savedModulesOrder = localStorage.getItem(STORAGE_KEY_MODULES);
    if (savedModulesOrder) {
      try {
        setModulesOrder(JSON.parse(savedModulesOrder));
      } catch (e) {
        console.error("Failed to parse saved modules order:", e);
        initializeDefaultModulesOrder();
      }
    } else {
      initializeDefaultModulesOrder();
    }

    // порядок репортов
    const savedReportsOrder = localStorage.getItem(STORAGE_KEY_REPORTS);
    if (savedReportsOrder) {
      try {
        setReportsOrder(JSON.parse(savedReportsOrder));
      } catch (e) {
        console.error("Failed to parse saved reports order:", e);
        initializeDefaultReportsOrder();
      }
    } else {
      initializeDefaultReportsOrder();
    }
  }, [systemModules.length, regularModules.length, reportModules.length]);

  const initializeDefaultSystemOrder = () => {
    if (systemModules.length > 0) {
      const defaultOrder = systemModules.map((m) => String(m.id));
      setSystemOrder(defaultOrder);
      localStorage.setItem(STORAGE_KEY_SYSTEM, JSON.stringify(defaultOrder));
    }
  };

  const initializeDefaultModulesOrder = () => {
    if (regularModules.length > 0) {
      const defaultOrder = regularModules.map((m) => String(m.id));
      setModulesOrder(defaultOrder);
      localStorage.setItem(STORAGE_KEY_MODULES, JSON.stringify(defaultOrder));
    }
  };

  const initializeDefaultReportsOrder = () => {
    if (reportModules.length > 0) {
      const defaultOrder = reportModules.map((m) => String(m.id));
      setReportsOrder(defaultOrder);
      localStorage.setItem(STORAGE_KEY_REPORTS, JSON.stringify(defaultOrder));
    }
  };

  const handleSystemOrderChange = (newOrder: string[]) => {
    setSystemOrder(newOrder);
    localStorage.setItem(STORAGE_KEY_SYSTEM, JSON.stringify(newOrder));
  };

  const handleModulesOrderChange = (newOrder: string[]) => {
    setModulesOrder(newOrder);
    localStorage.setItem(STORAGE_KEY_MODULES, JSON.stringify(newOrder));
  };

  const handleReportsOrderChange = (newOrder: string[]) => {
    setReportsOrder(newOrder);
    localStorage.setItem(STORAGE_KEY_REPORTS, JSON.stringify(newOrder));
  };

  const getOrderedItems = (items: typeof modules, order: string[]) => {
    if (order.length === 0) return items;

    const ordered = order
      .map((id) => items.find((m) => String(m.id) === id))
      .filter(Boolean);

    const newItems = items.filter(
      (m) => !order.includes(String(m.id))
    );

    return [...ordered, ...newItems] as typeof items;
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      ToastService.success(t("orderSaved") || "Order saved successfully!");
    }
    setIsEditMode(!isEditMode);
  };

  const availableSystemBlocks = systemModules.map((module) => ({
    id: String(module.id),
    label: module.name,
    icon: "cog",
  }));

  const availableModulesBlocks = regularModules.map((module) => ({
    id: String(module.id),
    label: module.name,
    icon: "file",
  }));

  const availableReportsBlocks = reportModules.map((module) => ({
    id: String(module.id),
    label: module.name,
    icon: "chart-line",
  }));

  const orderedSystem = getOrderedItems(systemModules, systemOrder);
  const orderedModules = getOrderedItems(regularModules, modulesOrder);
  const orderedReports = getOrderedItems(reportModules, reportsOrder);

  const hasDashboardAccess = menuSettings.dashboard?.some(m => m.key === "dashboard") ?? false;

  if (!hasDashboardAccess) {
    return (
      <div className={styles.container}>
        <div className={styles.noAccess}>
          <h2>{t("noAccess") || "Access Denied"}</h2>
          <p>{t("contactAdmin") || "Please contact your administrator"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <p>{t("welcomeMessage")}</p>
        </div>
        <IconButton 
          onClick={toggleEditMode} 
          color="primary"
          className={styles.editButton}
          sx={{
            background: 'var(--primary)',
            color: 'white',
            '&:hover': {
              background: 'var(--primary)',
              opacity: 0.9,
            }
          }}
        >
          {isEditMode ? <SaveIcon /> : <EditIcon />}
        </IconButton>
      </div>

      {orderedSystem.length > 0 && (
        <div className={styles.systemSection}>
          <h2 className={styles.sectionTitle}>{t("systemModules")}</h2>
          {loading ? (
            <div className={styles.loader}>Loading...</div>
          ) : isEditMode ? (
            <BlockGridInput
              value={systemOrder}
              onChange={handleSystemOrderChange}
              availableBlocks={availableSystemBlocks}
              gridColumns={3}
              label={t("arrangeSystemModules")}
              gridClassName={styles.moduleGrid}
              blockClassName={styles.moduleCard}
              blockIconClassName={styles.moduleIcon}
              blockLabelClassName={styles.moduleContent}
            />
          ) : (
            <div className={styles.moduleGrid}>
              {orderedSystem.map((module) => (
                <div
                  key={module.id}
                  className={styles.moduleCard}
                  onClick={() => navigate(`/modules/${module.id}`)}
                >
                  <div className={styles.moduleIcon}>
                    <FontAwesomeIcon icon={getIcon("cog")} />
                  </div>
                  <div className={styles.moduleContent}>
                    <h3>{module.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {orderedSystem.length > 0 && orderedModules.length > 0 && (
        <div className={styles.divider}></div>
      )}

      {orderedModules.length > 0 && (
        <div className={styles.modulesSection}>
          <h2 className={styles.sectionTitle}>{t("modules")}</h2>
          {loading ? (
            <div className={styles.loader}>Loading...</div>
          ) : isEditMode ? (
            <BlockGridInput
              value={modulesOrder}
              onChange={handleModulesOrderChange}
              availableBlocks={availableModulesBlocks}
              gridColumns={3}
              label={t("arrangeModules")}
              gridClassName={styles.moduleGrid}
              blockClassName={styles.moduleCard}
              blockIconClassName={styles.moduleIcon}
              blockLabelClassName={styles.moduleContent}
            />
          ) : (
            <div className={styles.moduleGrid}>
              {orderedModules.map((module) => (
                <div
                  key={module.id}
                  className={styles.moduleCard}
                  onClick={() => navigate(`/modules/${module.id}`)}
                >
                  <div className={styles.moduleIcon}>
                    <FontAwesomeIcon icon={getIcon("file")} />
                  </div>
                  <div className={styles.moduleContent}>
                    <h3>{module.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {orderedReports.length > 0 && (orderedModules.length > 0 || orderedSystem.length > 0) && (
        <div className={styles.divider}></div>
      )}

      {orderedReports.length > 0 && (
        <div className={styles.reportsSection}>
          <h2 className={styles.sectionTitle}>{t("reports")}</h2>
          {loading ? (
            <div className={styles.loader}>Loading...</div>
          ) : isEditMode ? (
            <BlockGridInput
              value={reportsOrder}
              onChange={handleReportsOrderChange}
              availableBlocks={availableReportsBlocks}
              gridColumns={3}
              label={t("arrangeReports")}
              gridClassName={styles.moduleGrid}
              blockClassName={styles.moduleCard}
              blockIconClassName={styles.moduleIcon}
              blockLabelClassName={styles.moduleContent}
            />
          ) : (
            <div className={styles.moduleGrid}>
              {orderedReports.map((module) => (
                <div
                  key={module.id}
                  className={styles.moduleCard}
                  onClick={() => navigate(`/modules/${module.id}`)}
                >
                  <div className={styles.moduleIcon}>
                    <FontAwesomeIcon icon={getIcon("chart-line")} />
                  </div>
                  <div className={styles.moduleContent}>
                    <h3>{module.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}